import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { nav, company } from '../mock';
import logoMark from '../assets/logo-mark.png';

export const ElementMark = ({ light = false, size = 'md' }) => {
  const box = size === 'sm' ? 'w-10 h-10' : 'w-12 h-12';
  return (
    <img
      src={logoMark}
      alt={`${company.fullName} logo`}
      className={`${box} object-contain shrink-0 select-none`}
      draggable="false"
    />
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  const solid = scrolled || !isHome;
  const textColor = solid ? 'text-neutral-900' : 'text-white';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? 'bg-white/90 backdrop-blur-md border-b border-neutral-200 py-3' : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group shrink-0">
          <ElementMark light={!solid} size="sm" />
          <div className={`flex flex-col justify-center gap-1 ${textColor} transition-colors duration-500`}>
            <div className="font-display font-bold tracking-[0.18em] text-base leading-none md:text-lg md:leading-none whitespace-nowrap">WHITEMOON</div>
            <div className="eyebrow opacity-60 text-[0.6rem] leading-none">Magnesium</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 ml-10">
          {nav.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`eyebrow text-[0.62rem] xl:text-[0.72rem] tracking-[0.2em] xl:tracking-[0.28em] whitespace-nowrap relative transition-colors duration-300 ${textColor} ${
                  active ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                }`}
              >
                {item.label}
                <span
                  className={`absolute -bottom-2 left-0 h-px bg-amber-700 transition-all duration-300 ${
                    active ? 'w-full' : 'w-0'
                  }`}
                />
              </Link>
            );
          })}
        </nav>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button aria-label="Open menu" className={`${textColor}`}>
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-white">
              <div className="flex items-center gap-3 mt-2 mb-10">
                <ElementMark size="sm" />
                <span className="font-display font-bold tracking-[0.2em]">WHITEMOON</span>
              </div>
              <nav className="flex flex-col gap-6">
                {nav.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setOpen(false)}
                    className={`eyebrow text-neutral-900 ${
                      location.pathname === item.path ? 'text-amber-700' : 'opacity-70'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
